import React, { useState, useMemo, useRef, useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { motion, AnimatePresence } from "framer-motion";
import {
  Copy,
  Check,
  ExternalLink,
  QrCode,
  MessageSquare,
  Phone,
  Sparkles,
  Share2,
  Download,
  Bold,
  Italic,
  Strikethrough,
  Code,
  Zap,
  ArrowRight,
  ChevronDown,
  Info,
  Smartphone,
  CheckCircle2,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from "@/hooks/use-toast";

// Country prefixes list
const COUNTRIES = [
  {
    name: "Argentina",
    code: "AR",
    prefix: "549",
    flag: "🇦🇷",
    placeholder: "11 2372 9528",
  },
  {
    name: "México",
    code: "MX",
    prefix: "52",
    flag: "🇲🇽",
    placeholder: "55 1234 5678",
  },
  {
    name: "Colombia",
    code: "CO",
    prefix: "57",
    flag: "🇨🇴",
    placeholder: "300 123 4567",
  },
  {
    name: "Chile",
    code: "CL",
    prefix: "569",
    flag: "🇨🇱",
    placeholder: "9 1234 5678",
  },
  {
    name: "España",
    code: "ES",
    prefix: "34",
    flag: "🇪🇸",
    placeholder: "612 345 678",
  },
  {
    name: "Perú",
    code: "PE",
    prefix: "51",
    flag: "🇵🇪",
    placeholder: "912 345 678",
  },
  {
    name: "Uruguay",
    code: "UY",
    prefix: "598",
    flag: "🇺🇾",
    placeholder: "99 123 456",
  },
  {
    name: "Paraguay",
    code: "PY",
    prefix: "595",
    flag: "🇵🇾",
    placeholder: "981 123 456",
  },
  {
    name: "Ecuador",
    code: "EC",
    prefix: "593",
    flag: "🇪🇨",
    placeholder: "99 123 4567",
  },
  {
    name: "Estados Unidos",
    code: "US",
    prefix: "1",
    flag: "🇺🇸",
    placeholder: "202 555 0123",
  },
  {
    name: "Otro / Personalizado",
    code: "CUSTOM",
    prefix: "",
    flag: "🌐",
    placeholder: "Código de país + número",
  },
];

// Pre-made templates
const TEMPLATES = [
  {
    label: "Consulta General",
    text: "Hola! Vengo de la web y quisiera hacer una consulta.",
    icon: "💬",
  },
  {
    label: "Solicitar Precios / Catálogo",
    text: "Hola! Me gustaría recibir información de precios y catálogo actualizado.",
    icon: "🏷️",
  },
  {
    label: "Agendar Turno / Cita",
    text: "Hola! Quiero consultar la disponibilidad de turnos para esta semana.",
    icon: "📅",
  },
  {
    label: "Soporte Técnico",
    text: "Hola! Tengo una consulta sobre mi servicio y necesito asistencia.",
    icon: "🛠️",
  },
  {
    label: "Solicitar Presupuesto",
    text: "Hola! Quisiera solicitar un presupuesto personalizado para mi empresa.",
    icon: "📊",
  },
];

export default function GeneradorWhatsapp() {
  const { toast } = useToast();
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [customPrefix, setCustomPrefix] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState(
    "Hola! Vengo de la web y quisiera información sobre sus servicios.",
  );
  const [copied, setCopied] = useState(false);
  const [qrColor, setQrColor] = useState<"dark" | "brand">("brand");
  const [qrDataUrl, setQrDataUrl] = useState<string>("");

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Clean phone number to digits only
  const cleanPhone = useMemo(() => {
    let raw = phone.replace(/\D/g, "");
    if (!raw) return "";

    const prefix =
      selectedCountry.code === "CUSTOM"
        ? customPrefix.replace(/\D/g, "")
        : selectedCountry.prefix;

    // Argentina specific check
    if (selectedCountry.prefix === "549") {
      if (raw.startsWith("549")) return raw;
      if (raw.startsWith("54")) return "549" + raw.slice(2);
      // If user typed "9" (mobile indicator), just add country code "54"
      if (raw.startsWith("9")) return "54" + raw;
      if (raw.startsWith("0")) raw = raw.slice(1);
      // Remove "15" mobile indicator (old format: 011 15 ...)
      if (raw.startsWith("15")) raw = raw.slice(2);
      // Check for "15" after 2-digit area code (e.g. "11 15 ...")
      if (raw.length > 10 && raw.slice(2, 4) === "15")
        raw = raw.slice(0, 2) + raw.slice(4);
      // Check for "15" after 3-digit area code (e.g. "261 15 ...")
      if (raw.length > 10 && raw.slice(3, 5) === "15")
        raw = raw.slice(0, 3) + raw.slice(5);
      return "549" + raw;
    }

    // Standard prefix appending
    if (prefix && !raw.startsWith(prefix)) {
      // For prefixes ending in "9" (mobile indicator, e.g. Chile "569")
      const mobileIndicator = prefix.slice(-1);
      const countryCode = prefix.slice(0, -1);
      if (mobileIndicator === "9" && countryCode) {
        // If raw starts with country code but not full prefix, insert mobile indicator
        if (raw.startsWith(countryCode))
          return prefix + raw.slice(countryCode.length);
        // If raw starts with mobile indicator "9", add just the country code
        if (raw.startsWith("9")) return countryCode + raw;
      }
      return prefix + raw;
    }
    return raw;
  }, [phone, selectedCountry, customPrefix]);

  // Generated Link
  const generatedLink = useMemo(() => {
    if (!cleanPhone) return "";
    const encodedText = encodeURIComponent(message.trim());
    return `https://wa.me/${cleanPhone}${encodedText ? `?text=${encodedText}` : ""}`;
  }, [cleanPhone, message]);

  // Generate QR Code using QuickChart QR API / SVG
  useEffect(() => {
    if (!generatedLink) {
      setQrDataUrl("");
      return;
    }
    const colorHex = qrColor === "brand" ? "7C3AED" : "000000";
    const bgHex = "FFFFFF";
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(
      generatedLink,
    )}&color=${colorHex}&bgcolor=${bgHex}&margin=10`;
    setQrDataUrl(qrUrl);
  }, [generatedLink, qrColor]);

  const handleCopyLink = async () => {
    if (!generatedLink) {
      toast({
        title: "Ingresá un número de teléfono",
        description:
          "Por favor completá tu número de WhatsApp para generar el enlace.",
        variant: "destructive",
      });
      return;
    }

    let success = false;

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(generatedLink);
        success = true;
      } catch (err) {
        console.warn(
          "Clipboard API unavailable or blocked, trying fallback copy:",
          err,
        );
      }
    }

    if (!success) {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = generatedLink;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        success = document.execCommand("copy");
        document.body.removeChild(textArea);
      } catch (err) {
        console.error("Fallback copy failed:", err);
      }
    }

    if (success) {
      setCopied(true);
      toast({
        title: "¡Enlace copiado!",
        description: "El link de WhatsApp ya está listo en tu portapapeles.",
      });
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast({
        title: "No se pudo copiar automáticamente",
        description: "Por favor selecciona y copia el enlace manualmente.",
        variant: "destructive",
      });
    }
  };

  const handleFormatText = (symbol: string) => {
    if (!textareaRef.current) return;
    const input = textareaRef.current;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const selectedText = message.substring(start, end);

    let formattedText = "";
    if (selectedText) {
      formattedText = `${symbol}${selectedText}${symbol}`;
    } else {
      formattedText = `${symbol}texto${symbol}`;
    }

    const newMessage =
      message.substring(0, start) + formattedText + message.substring(end);
    setMessage(newMessage);

    setTimeout(() => {
      input.focus();
      input.setSelectionRange(
        start + symbol.length,
        end ? end + symbol.length : start + symbol.length + 5,
      );
    }, 50);
  };

  const downloadQR = async () => {
    if (!qrDataUrl) return;
    try {
      const response = await fetch(qrDataUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `whatsapp-qr-crecelocal-${cleanPhone || "link"}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({
        title: "¡Código QR descargado!",
        description: "Podés imprimirlo o sumarlo a tus piezas gráficas.",
      });
    } catch (e) {
      window.open(qrDataUrl, "_blank");
    }
  };

  // WhatsApp Chat formatted text view
  const renderFormattedPreview = (text: string) => {
    if (!text)
      return (
        <span className="text-white/40 italic">
          Escribí tu mensaje arriba...
        </span>
      );

    // Convert WhatsApp markdown to styled elements
    let parsed = text
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      .replace(/_(.*?)_/g, "<em>$1</em>")
      .replace(/~(.*?)~/g, "<del>$1</del>")
      .replace(
        /```(.*?)```/g,
        "<code className='bg-black/20 px-1 rounded font-mono'>$1</code>",
      );

    return (
      <span
        dangerouslySetInnerHTML={{ __html: parsed.replace(/\n/g, "<br/>") }}
      />
    );
  };

  // Get current time formatted HH:MM
  const currentTime = useMemo(() => {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }, []);

  return (
    <div className="min-h-screen bg-[#08080A] text-[#FAFAFA] font-sans antialiased selection:bg-[#7C3AED] selection:text-white">
      <SEOHead
        title="Generador de Links de WhatsApp Gratis con Mensaje | CreceLocal"
        description="Creá tu enlace wa.me de WhatsApp con mensaje personalizado y código QR listo en segundos. Ideal para Bio de Instagram, Meta Ads y sitio web."
        canonical="/generador-link-whatsapp"
      />

      {/* Schema.org Tool Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Generador de Enlaces de WhatsApp CreceLocal",
            url: "https://crecelocal.com/generador-link-whatsapp",
            description:
              "Herramienta gratuita para generar links wa.me personalizados de WhatsApp con mensaje predeterminado y código QR descargable.",
            applicationCategory: "BusinessApplication",
            operatingSystem: "All",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
          }),
        }}
      />

      <Navbar />

      <main className="pt-28 pb-20 px-4 md:px-8 max-w-[1240px] mx-auto">
        {/* Header Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/30 text-[#A78BFA] text-xs md:text-sm font-medium mb-5">
            <Sparkles className="w-4 h-4 text-[#7C3AED]" />
            <span>Herramienta Gratuita de Marketing 100% Libre</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 text-[#FAFAFA] leading-[1.15]">
            Generador de Links de{" "}
            <span className="bg-gradient-to-r from-[#7C3AED] to-[#FB7185] bg-clip-text text-transparent">
              WhatsApp
            </span>{" "}
            con Mensaje
          </h1>

          <p className="text-[#A1A1AA] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Creá tu enlace{" "}
            <code className="text-[#A78BFA] bg-[#131316] px-1.5 py-0.5 rounded font-mono text-sm">
              wa.me
            </code>{" "}
            con número y texto predeterminado en segundos. Incluye código QR
            descargable para tus piezas impresas o digitales.
          </p>
        </div>

        {/* Main Generator Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-20">
          {/* Left Column: Generator Form (7 cols) */}
          <div className="lg:col-span-7 bg-[#0F0F11] border border-white/10 rounded-2xl p-5 sm:p-8 shadow-2xl space-y-6">
            <div className="border-b border-white/10 pb-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-[#7C3AED]" />
                1. Ingresá tu número de WhatsApp
              </h2>
              <p className="text-xs text-[#A1A1AA] mt-1">
                Elegí tu país e ingresá tu número de teléfono comercial.
              </p>
            </div>

            {/* Country Selector + Phone Input */}
            <div className="space-y-3">
              <label className="text-xs font-medium text-[#A1A1AA] uppercase tracking-wider block">
                País y Código de Área
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-5 relative">
                  <select
                    value={selectedCountry.code}
                    onChange={(e) => {
                      const c = COUNTRIES.find(
                        (item) => item.code === e.target.value,
                      );
                      if (c) setSelectedCountry(c);
                    }}
                    className="w-full bg-[#131316] text-white border border-white/10 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#7C3AED] transition-colors appearance-none cursor-pointer pr-8"
                  >
                    {COUNTRIES.map((c) => (
                      <option
                        key={c.code}
                        value={c.code}
                        className="bg-[#0F0F11] text-white"
                      >
                        {c.flag} {c.name} {c.prefix ? `(+${c.prefix})` : ""}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 text-white/50 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>

                <div className="sm:col-span-7">
                  {selectedCountry.code === "CUSTOM" ? (
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="Ej: 549"
                        value={customPrefix}
                        onChange={(e) => setCustomPrefix(e.target.value)}
                        className="w-24 bg-[#131316] border-white/10 text-white placeholder:text-white/30 rounded-xl py-6"
                      />
                      <Input
                        type="text"
                        placeholder="Número de celular"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="bg-[#131316] border-white/10 text-white placeholder:text-white/30 rounded-xl py-6"
                      />
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/50 text-sm font-mono pointer-events-none">
                        +{selectedCountry.prefix}
                      </div>
                      <Input
                        type="text"
                        placeholder={selectedCountry.placeholder}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="bg-[#131316] border-white/10 text-white placeholder:text-white/30 rounded-xl pl-16 py-6 font-mono text-base focus:border-[#7C3AED] transition-all"
                      />
                    </div>
                  )}
                </div>
              </div>

              {selectedCountry.prefix === "549" && (
                <p className="text-[11px] text-[#A1A1AA] flex items-center gap-1.5 pt-1">
                  <Info className="w-3.5 h-3.5 text-[#7C3AED] shrink-0" />
                  <span>
                    Para Argentina se agrega automáticamente el prefijo
                    internacional <strong className="text-white">+54 9</strong>.
                  </span>
                </p>
              )}
            </div>

            {/* Message Input & formatting */}
            <div className="border-t border-white/10 pt-6 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#7C3AED]" />
                  2. Escribí tu mensaje predeterminado
                </h2>
                <span className="text-xs text-[#A1A1AA] font-mono">
                  {message.length} caracteres
                </span>
              </div>

              {/* Quick Template Chips */}
              <div>
                <p className="text-xs text-[#A1A1AA] mb-2 font-medium">
                  Plantillas rápidas:
                </p>
                <div className="flex flex-wrap gap-2">
                  {TEMPLATES.map((tmpl) => (
                    <button
                      key={tmpl.label}
                      type="button"
                      onClick={() => setMessage(tmpl.text)}
                      className="text-xs bg-[#131316] hover:bg-[#7C3AED]/20 hover:border-[#7C3AED]/40 text-[#A1A1AA] hover:text-white border border-white/10 rounded-lg px-2.5 py-1.5 transition-all flex items-center gap-1.5"
                    >
                      <span>{tmpl.icon}</span>
                      <span>{tmpl.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Text formatting toolbar */}
              <div className="flex items-center gap-1 bg-[#131316] border border-white/10 rounded-t-xl px-3 py-2 border-b-0">
                <span className="text-xs text-[#A1A1AA] mr-2 hidden sm:inline">
                  Formato de texto:
                </span>
                <button
                  type="button"
                  onClick={() => handleFormatText("*")}
                  title="Negrita (*texto*)"
                  className="p-1.5 hover:bg-white/10 rounded text-white/70 hover:text-white transition-colors text-xs flex items-center gap-1 font-bold"
                >
                  <Bold className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Negrita</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleFormatText("_")}
                  title="Cursiva (_texto_)"
                  className="p-1.5 hover:bg-white/10 rounded text-white/70 hover:text-white transition-colors text-xs flex items-center gap-1 italic"
                >
                  <Italic className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Cursiva</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleFormatText("~")}
                  title="Tachado (~texto~)"
                  className="p-1.5 hover:bg-white/10 rounded text-white/70 hover:text-white transition-colors text-xs flex items-center gap-1 line-through"
                >
                  <Strikethrough className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Tachado</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleFormatText("```")}
                  title="Monoespaciado (```texto```)"
                  className="p-1.5 hover:bg-white/10 rounded text-white/70 hover:text-white transition-colors text-xs flex items-center gap-1 font-mono"
                >
                  <Code className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Código</span>
                </button>
              </div>

              <Textarea
                ref={textareaRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Hola! Vengo de la web y quisiera consultar sobre..."
                className="bg-[#131316] border-white/10 text-white placeholder:text-white/30 rounded-b-xl rounded-t-none min-h-[110px] focus:border-[#7C3AED] transition-all text-sm leading-relaxed"
              />
            </div>

            {/* Generated Link Action Box */}
            <div className="border-t border-white/10 pt-6 space-y-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#7C3AED]" />
                3. Tu Enlace Generado
              </h2>

              <div className="bg-[#131316] border border-white/10 rounded-xl p-3.5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="font-mono text-xs text-[#A78BFA] break-all max-h-16 overflow-y-auto pr-2">
                  {generatedLink ||
                    "https://wa.me/5491123729528?text=Ingres%C3%A1%20tu%20n%C3%BAmero"}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    onClick={handleCopyLink}
                    disabled={!generatedLink}
                    className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-medium text-xs rounded-lg h-10 px-4 transition-all shadow-lg shadow-[#7C3AED]/20"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-1.5 text-emerald-400" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1.5" />
                        Copiar Enlace
                      </>
                    )}
                  </Button>

                  {generatedLink && (
                    <a
                      href={generatedLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        import("@/lib/analytics").then(({ trackWhatsApp }) =>
                          trackWhatsApp(
                            "Generador - Probar enlace",
                            generatedLink,
                          ),
                        )
                      }
                      className="bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/30 font-medium text-xs rounded-lg h-10 px-3.5 flex items-center justify-center transition-all"
                      title="Probar en WhatsApp"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Live Mobile Chat Preview & QR Code (5 cols) */}
          <div className="lg:col-span-5 space-y-6">
            {/* WhatsApp Simulated Phone Box */}
            <div className="bg-[#0F0F11] border border-white/10 rounded-2xl p-5 shadow-2xl relative overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-xs font-medium text-white/80 uppercase tracking-wider">
                    Vista Previa en WhatsApp
                  </span>
                </div>
                <span className="text-[11px] text-[#A1A1AA]">
                  Simulador en tiempo real
                </span>
              </div>

              {/* Phone Frame mock */}
              <div className="bg-[#0b141a] border border-white/10 rounded-xl overflow-hidden shadow-inner font-sans">
                {/* WhatsApp Chat Header */}
                <div className="bg-[#202c33] px-3 py-2.5 flex items-center justify-between text-white border-b border-white/5">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-[#00a884] flex items-center justify-center text-white font-bold text-xs">
                      {selectedCountry.flag}
                    </div>
                    <div>
                      <div className="text-xs font-semibold leading-tight text-white/95">
                        +{cleanPhone || selectedCountry.prefix + "..."}
                      </div>
                      <div className="text-[10px] text-emerald-400">
                        en línea
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-white/60 text-xs">
                    <Phone className="w-3.5 h-3.5" />
                    <span className="text-xs">⋮</span>
                  </div>
                </div>

                {/* WhatsApp Chat Body */}
                <div className="p-4 min-h-[160px] bg-[radial-gradient(#111b21_1px,transparent_1px)] [background-size:16px_16px] flex flex-col justify-end">
                  <div className="self-end max-w-[85%] bg-[#005c4b] text-white rounded-lg rounded-tr-none px-3 py-2 shadow text-xs leading-relaxed relative">
                    <div className="pr-8 text-white/95">
                      {renderFormattedPreview(message)}
                    </div>
                    <div className="text-[9px] text-white/60 text-right mt-1 font-mono flex items-center justify-end gap-1">
                      <span>{currentTime}</span>
                      <span className="text-emerald-300">✓✓</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* QR Code Section */}
            <div className="bg-[#0F0F11] border border-white/10 rounded-2xl p-5 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-[#7C3AED]" />
                  Código QR para tu Enlace
                </h3>

                <div className="flex items-center gap-1 bg-[#131316] p-1 rounded-lg border border-white/10 text-xs">
                  <button
                    type="button"
                    onClick={() => setQrColor("brand")}
                    className={`px-2 py-0.5 rounded ${
                      qrColor === "brand"
                        ? "bg-[#7C3AED] text-white"
                        : "text-white/60 hover:text-white"
                    }`}
                  >
                    Violeta
                  </button>
                  <button
                    type="button"
                    onClick={() => setQrColor("dark")}
                    className={`px-2 py-0.5 rounded ${
                      qrColor === "dark"
                        ? "bg-white/20 text-white"
                        : "text-white/60 hover:text-white"
                    }`}
                  >
                    Negro
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 bg-[#131316] p-4 rounded-xl border border-white/10">
                <div className="bg-white p-2 rounded-lg shrink-0 shadow-lg">
                  {qrDataUrl ? (
                    <img
                      src={qrDataUrl}
                      alt="Código QR de WhatsApp"
                      className="w-28 h-28 object-contain"
                    />
                  ) : (
                    <div className="w-28 h-28 flex items-center justify-center text-black/30 text-xs text-center font-mono">
                      Ingresá tu número
                    </div>
                  )}
                </div>

                <div className="space-y-2 text-center sm:text-left">
                  <p className="text-xs text-[#A1A1AA] leading-relaxed">
                    Usa este QR en carteles, folletos, tarjetas de presentación
                    o mostradores para que te escriban escaneando con la cámara.
                  </p>

                  <Button
                    onClick={downloadQR}
                    disabled={!qrDataUrl}
                    variant="outline"
                    className="w-full sm:w-auto bg-[#0F0F11] hover:bg-white/10 border-white/20 text-white text-xs h-8 px-3"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Descargar QR (PNG)
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Banner: Automate WhatsApp with CreceLocal */}
        <section className="my-16 bg-gradient-to-r from-[#131316] via-[#0F0F11] to-[#131316] border border-[#7C3AED]/30 rounded-2xl p-6 md:p-10 relative overflow-hidden shadow-2xl">
          <div className="absolute -right-20 -bottom-20 w-80 h-80 bg-[#7C3AED]/10 rounded-full blur-3xl pointer-events-none" />

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center relative z-10">
            <div className="md:col-span-8 space-y-3">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#A78BFA] bg-[#7C3AED]/20 px-3 py-1 rounded-full border border-[#7C3AED]/30">
                <Zap className="w-3.5 h-3.5 text-[#7C3AED]" />
                ¿Recibís muchas consultas por WhatsApp?
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">
                Respondé a todas las consultas de tu enlace en segundos con IA
              </h2>

              <p className="text-[#A1A1AA] text-sm md:text-base leading-relaxed max-w-xl">
                CreceLocal conecta tus enlaces de WhatsApp con un Agente de
                Inteligencia Artificial entrenado con tu tono e información
                comercial. Atiende 24/7, califica los leads y cierra ventas
                automáticamente.
              </p>
            </div>

            <div className="md:col-span-4 flex md:justify-end">
              <a href="/diagnostico">
                <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-medium text-sm rounded-xl py-6 px-6 shadow-xl shadow-[#7C3AED]/25 transition-all w-full md:w-auto flex items-center justify-center gap-2">
                  <span>Diagnóstico Comercial Gratis</span>
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Educational Content & Best Practices Section */}
        <section className="space-y-12 mb-16">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              ¿Cómo aprovechar al máximo tu link de WhatsApp?
            </h2>
            <p className="text-[#A1A1AA] text-sm">
              Estrategias prácticas para convertir visitas en clientes en tus
              canales digitales.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#0F0F11] border border-white/10 rounded-xl p-6 space-y-3">
              <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/10 border border-[#7C3AED]/20 flex items-center justify-center text-[#A78BFA] font-bold">
                1
              </div>
              <h3 className="text-lg font-semibold text-white">
                Bio de Instagram y TikTok
              </h3>
              <p className="text-xs text-[#A1A1AA] leading-relaxed">
                Colocá tu link directo en la biografía. Al incluir un mensaje
                predeterminado como{" "}
                <em>
                  "Hola! Vi tu perfil de Instagram y quiero consultar por..."
                </em>{" "}
                sabrás exactamente de dónde viene el prospecto.
              </p>
            </div>

            <div className="bg-[#0F0F11] border border-white/10 rounded-xl p-6 space-y-3">
              <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/10 border border-[#7C3AED]/20 flex items-center justify-center text-[#A78BFA] font-bold">
                2
              </div>
              <h3 className="text-lg font-semibold text-white">
                Anuncios en Meta & Google Ads
              </h3>
              <p className="text-xs text-[#A1A1AA] leading-relaxed">
                Si hacés pauta publicitaria en Facebook o Instagram Ads, dirigir
                el tráfico a WhatsApp mediante un enlace con mensaje específico
                aumenta la conversión hasta un 40% frente a enlaces genéricos.
              </p>
            </div>

            <div className="bg-[#0F0F11] border border-white/10 rounded-xl p-6 space-y-3">
              <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/10 border border-[#7C3AED]/20 flex items-center justify-center text-[#A78BFA] font-bold">
                3
              </div>
              <h3 className="text-lg font-semibold text-white">
                Material Impreso con Código QR
              </h3>
              <p className="text-xs text-[#A1A1AA] leading-relaxed">
                Descargá el código QR en alta calidad e imprimilo en packaging,
                manteles, mostradores o volantes. El cliente escanea con la
                cámara de su celular e inicia la charla de inmediato.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-[#0F0F11] border border-white/10 rounded-2xl p-6 md:p-10 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <HelpCircle className="w-6 h-6 text-[#7C3AED]" />
            <h2 className="text-2xl font-bold text-white">
              Preguntas Frecuentes sobre el Generador
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-3">
            <AccordionItem value="item-1" className="border-white/10">
              <AccordionTrigger className="text-white text-sm hover:text-[#A78BFA] text-left">
                ¿Es totalmente gratis este generador de links de WhatsApp?
              </AccordionTrigger>
              <AccordionContent className="text-[#A1A1AA] text-xs leading-relaxed">
                Sí, es 100% gratuito e ilimitado. Podés generar todos los
                enlaces y códigos QR que necesites para tu negocio sin ningún
                costo ni registro.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border-white/10">
              <AccordionTrigger className="text-white text-sm hover:text-[#A78BFA] text-left">
                ¿Los links creados expiran o caducan?
              </AccordionTrigger>
              <AccordionContent className="text-[#A1A1AA] text-xs leading-relaxed">
                No. Los enlaces generados utilizan la estructura oficial{" "}
                <code className="text-[#A78BFA] bg-black/30 px-1">wa.me</code>{" "}
                directamente provista por WhatsApp (Meta). No caducan nunca y
                funcionarán mientras tu número de teléfono esté activo.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border-white/10">
              <AccordionTrigger className="text-white text-sm hover:text-[#A78BFA] text-left">
                ¿Funciona con WhatsApp Normal y WhatsApp Business?
              </AccordionTrigger>
              <AccordionContent className="text-[#A1A1AA] text-xs leading-relaxed">
                Sí, funciona perfectamente con ambas versiones de WhatsApp en
                Android, iPhone, tabletas y WhatsApp Web / Desktop.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-[#4]" className="border-white/10">
              <AccordionTrigger className="text-white text-sm hover:text-[#A78BFA] text-left">
                ¿Cómo puedo automatizar las respuestas cuando los clientes hacen
                clic en el link?
              </AccordionTrigger>
              <AccordionContent className="text-[#A1A1AA] text-xs leading-relaxed">
                En CreceLocal implementamos Agentes de Inteligencia Artificial
                que se integran con tu WhatsApp para responder inmediatamente,
                calificar al cliente y agendar turnos o tomar pedidos
                automáticamente las 24 horas. Podés solicitar un{" "}
                <a href="/diagnostico" className="text-[#A78BFA] underline">
                  diagnóstico comercial gratuito
                </a>{" "}
                para conocer cómo implementarlo.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>
      </main>

      <Footer />
    </div>
  );
}
